<script setup lang="ts">
import Skeleton from '~/components/ui/Skeleton.vue'
/**
 * Topbar dropdown for the session's scope: which organisation, which project.
 * Same control in both places (and in the organisation space), so a switch is one click
 * from every screen. Items: mark · name · role or meta · check on the active one; the
 * footer carries the two actions the source offers (settings · create).
 * Wire: organisations from GET /me + PUT /me/active-organization; projects from useProjectContext().
 */
import { ref, onMounted, onUnmounted } from 'vue'
import Icon from '~/components/ui/Icon.vue'
import type { IconName } from '~/utils/icons'

interface SwitcherItem { id: string; name: string; meta?: string; active?: boolean }
interface SwitcherAction { id?: string; label: string; icon: IconName; to?: string; primary?: boolean }

withDefaults(defineProps<{
  /** Heading of the list: Organisations · Projects. */
  label: string
  items: SwitcherItem[]
  actions?: SwitcherAction[]
  /** mark = the letter tile (organisations); icon = a lucide glyph (projects). */
  mark?: 'letter' | IconName
}>(), { actions: () => [], mark: 'letter' })
const emit = defineEmits<{ select: [item: SwitcherItem]; action: [action: SwitcherAction] }>()

const open = ref(false)
const popup = ref<HTMLElement | null>(null)
function toggle() {
  open.value=!open.value
  if(open.value) void nextTick(()=>popup.value?.querySelector<HTMLButtonElement>('button')?.focus())
}
function choose(action: SwitcherAction) { root.value?.querySelector<HTMLButtonElement>('button')?.focus(); open.value=false; emit('action',action) }
function key(event: KeyboardEvent) { if(event.key === 'Escape' && open.value) {open.value=false;root.value?.querySelector<HTMLButtonElement>('button')?.focus()} }

const root = ref<HTMLElement | null>(null)
const onDoc = (e: MouseEvent) => { if (root.value && !root.value.contains(e.target as Node) && !popup.value?.contains(e.target as Node)) open.value = false }
onMounted(() => { document.addEventListener('mousedown', onDoc); document.addEventListener('keydown', key) })
onUnmounted(() => { document.removeEventListener('mousedown', onDoc); document.removeEventListener('keydown', key) })
</script>

<template>
  <div ref="root" class="relative min-w-0">
    <button type="button" :aria-expanded="open" class="hit flex h-7 w-full min-w-0 max-w-[var(--measure-13rem)] items-center gap-1.5 rounded-[var(--radius-control)] px-1.5 text-xs text-muted-foreground hover:bg-muted hover:text-foreground" :class="open && 'bg-muted text-foreground'" @click.stop="toggle"><slot name="trigger">
      <!-- Before the list arrives the control shows placeholder bars the width of its eventual contents. -->
      <template v-if="!items.length">
        <Skeleton width="var(--measure-1rem)" height="var(--measure-1rem)" />
        <Skeleton width="var(--measure-60px)" />
      </template>
      <span v-else-if="mark === 'letter' && items.some(i => i.active)" class="grid size-4 shrink-0 place-items-center rounded-[var(--radius-compact)] bg-gradient-primary text-[length:var(--measure-0p5714rem)] font-bold text-primary-foreground">
        {{ (items.find(i => i.active)?.name ?? '').charAt(0).toUpperCase() }}
      </span>
      <Icon v-else-if="mark !== 'letter'" :name="mark" :size="13" class="shrink-0 text-primary" />
      <span v-if="items.length" class="truncate">{{ items.find(i => i.active)?.name ?? '' }}</span>
      <Icon name="chevron-down" :size="11" class="shrink-0" />
    </slot></button>

    <div v-if="open" ref="popup" class="absolute left-0 top-full z-50 mt-1 w-64 overflow-hidden rounded-lg border border-border bg-popover shadow-[var(--shadow-menu)]">
      <div class="flex h-7 items-center px-2.5 text-[length:var(--type-meta)] font-semibold text-muted-foreground">{{ label }}</div>
      <button
v-for="it in items" :key="it.id" type="button"
              class="flex h-8 w-full items-center gap-2 px-2.5 text-left text-xs hover:bg-sidebar-accent/60"
              :class="it.active && 'bg-sidebar-accent/40'" @click="emit('select', it); open = false">
        <span
v-if="mark === 'letter'" class="grid size-4 shrink-0 place-items-center rounded-[var(--radius-compact)] text-[length:var(--measure-0p5714rem)] font-bold"
              :class="it.active ? 'bg-gradient-primary text-primary-foreground' : 'bg-muted text-muted-foreground'">{{ it.name.charAt(0).toUpperCase() }}</span>
        <Icon v-else :name="mark" :size="13" class="shrink-0" :class="it.active ? 'text-primary' : 'text-muted-foreground'" />
        <span class="min-w-0 flex-1 truncate" :class="it.active ? 'font-medium text-foreground' : 'text-foreground'">{{ it.name }}</span>
        <span v-if="it.meta" class="num shrink-0 text-[length:var(--type-fine)] text-muted-foreground">{{ it.meta }}</span>
        <Icon v-if="it.active" name="check" :size="12" class="shrink-0 text-primary" />
      </button>
      <div v-if="actions.length" class="border-t border-border py-1">
        <button
v-for="a in actions" :key="a.label" type="button"
                class="flex h-8 w-full items-center gap-2 px-2.5 text-left text-xs hover:bg-sidebar-accent/60"
                @click="choose(a)">
          <Icon :name="a.icon" :size="12" class="shrink-0" :class="a.primary ? 'text-primary' : 'text-muted-foreground'" />{{ a.label }}
        </button>
      </div>
    </div>
  </div>
</template>
